<script setup lang="ts">
import { DialogModal, type Segment } from '@metapic-apps/shared'
import { computed, ref } from 'vue'

import { useAddCreatorToSegment, useRemoveCreatorFromSegment, useSearchStaticSegments } from '@/api'
import AddIcon from '@/assets/icons/add-plus.svg?component'
import RemoveIcon from '@/assets/icons/remove.svg?component'
import { BoxComponent, ButtonComponent, FlexComponent, InputComponent, LoadingComponent, TypographyComponent } from '@/ui-library'

type AddOption = { label: string; value: string }

const props = defineProps<{
  creatorId: number
  staticSegments: Segment[]
  dynamicSegments: Segment[]
}>()

const emits = defineEmits<{
  changed: []
}>()

const isOpen = defineModel<boolean>('modelValue', { default: false })

const { addToSegment } = useAddCreatorToSegment()
const { removeFromSegment } = useRemoveCreatorFromSegment()

const addSearch = ref('')
const removingId = ref<string | null>(null)
const { data: searchResults, isLoading: isSearching } = useSearchStaticSegments(addSearch)

const addOptions = computed<AddOption[]>(() =>
  searchResults.value
    .filter((segment) => !props.staticSegments.some((assigned) => assigned.id === segment.id))
    .map((segment) => ({ label: segment.name, value: segment.id })),
)

const onSelectSegment = async (option: AddOption) => {
  const success = await addToSegment(option.value, props.creatorId)
  if (success) {
    emits('changed')
  }
}

const onRemove = async (segmentId: string) => {
  removingId.value = segmentId
  const success = await removeFromSegment(segmentId, props.creatorId)
  removingId.value = null
  if (success) {
    emits('changed')
  }
}
</script>
<template>
  <DialogModal width="560px" align-center @before-close="() => (isOpen = false)">
    <template #header>
      <TypographyComponent variant="headline-medium">Edit segments</TypographyComponent>
    </template>
    <template #default>
      <FlexComponent direction="column" :gap="16" class="segments-body">
        <BoxComponent class="segments-card">
          <FlexComponent align="center" justify="space-between" :gap="8">
            <TypographyComponent variant="title-large">Static segments</TypographyComponent>
            <el-dropdown trigger="click" placement="bottom-end" :hide-on-click="false" popper-class="segments-add-dropdown">
              <ButtonComponent variant="secondary" data-testid="add-segment-trigger">
                <template #iconLeft>
                  <el-icon :size="24" color="var(--primary)">
                    <AddIcon />
                  </el-icon>
                </template>
                Add
              </ButtonComponent>
              <template #dropdown>
                <BoxComponent class="add-dropdown">
                  <BoxComponent :p="12">
                    <InputComponent v-model="addSearch" size="small" placeholder="Search segments" />
                  </BoxComponent>
                  <BoxComponent class="add-dropdown__list">
                    <BoxComponent v-if="isSearching" :p="16">
                      <LoadingComponent />
                    </BoxComponent>
                    <template v-else-if="addOptions.length">
                      <BoxComponent
                        v-for="option in addOptions"
                        :key="option.value"
                        class="add-dropdown__item"
                        data-testid="add-segment-option"
                        @click="onSelectSegment(option)"
                      >
                        <TypographyComponent variant="body-large">{{ option.label }}</TypographyComponent>
                      </BoxComponent>
                    </template>
                    <BoxComponent v-else :p="16">
                      <TypographyComponent variant="body-medium" color="var(--secondary-48)"> No segments found </TypographyComponent>
                    </BoxComponent>
                  </BoxComponent>
                </BoxComponent>
              </template>
            </el-dropdown>
          </FlexComponent>
          <BoxComponent :mt="16">
            <FlexComponent v-if="staticSegments.length" direction="column" :gap="8">
              <FlexComponent
                v-for="segment in staticSegments"
                :key="segment.id"
                align="center"
                justify="space-between"
                :gap="8"
                class="segment-item"
              >
                <TypographyComponent variant="label-large" class="segment-item__name">
                  {{ segment.name }}
                </TypographyComponent>
                <el-icon
                  :size="18"
                  color="var(--primary)"
                  class="segment-item__remove"
                  :class="{ 'segment-item__remove--disabled': removingId === segment.id }"
                  @click="removingId ? undefined : onRemove(segment.id)"
                >
                  <RemoveIcon />
                </el-icon>
              </FlexComponent>
            </FlexComponent>
            <TypographyComponent v-else variant="body-medium" color="var(--secondary-48)">
              No static segments assigned.
            </TypographyComponent>
          </BoxComponent>
        </BoxComponent>
        <BoxComponent class="segments-card">
          <TypographyComponent variant="title-large">Dynamic segments</TypographyComponent>
          <BoxComponent :mt="16">
            <FlexComponent v-if="dynamicSegments.length" direction="column" :gap="8">
              <FlexComponent v-for="segment in dynamicSegments" :key="segment.id" align="center" class="segment-item">
                <TypographyComponent variant="label-large" class="segment-item__name">
                  {{ segment.name }}
                </TypographyComponent>
              </FlexComponent>
            </FlexComponent>
            <TypographyComponent v-else variant="body-medium" color="var(--secondary-48)">
              No dynamic segments assigned.
            </TypographyComponent>
          </BoxComponent>
        </BoxComponent>
      </FlexComponent>
    </template>
  </DialogModal>
</template>
<style scoped lang="scss">
.segments-body {
  margin-top: 24px;
}
.segments-card {
  padding: 16px;
  background-color: var(--secondary-2);
  border-radius: 16px;
}
.segment-item {
  padding: 16px 12px 16px 16px;
  background-color: var(--inverted);
  border: 1px solid var(--secondary-8);
  border-radius: 8px;
}
.segment-item__name {
  flex: 1;
  min-width: 0;
  word-break: break-word;
}
.segment-item__remove {
  cursor: pointer;
  flex-shrink: 0;
  &:hover {
    opacity: 0.7;
  }
  &--disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
}
:deep(.el-icon path) {
  fill: currentColor;
}
</style>
<style lang="scss">
.segments-add-dropdown {
  .add-dropdown {
    width: 280px;
  }
  .add-dropdown__list {
    max-height: 320px;
    overflow-y: auto;
  }
  .add-dropdown__item {
    padding: 12px 16px;
    cursor: pointer;
    &:hover {
      background-color: var(--secondary-8);
    }
  }
}
</style>
